import { Position } from "../organisation/OrganisationModelV2"

export class MillPriceTier {
    name: string
    delta: number
    price: number
    cardinality: number
    price_pempem: number

    constructor(t: any) {
        this.name = t.name
        this.delta = t.delta
        this.price = t.price
        this.cardinality = t.cardinality
        this.price_pempem = t.price_pempem
    }

    static build(t: any): MillPriceTier {
        return new MillPriceTier(t)
    }
}

// A row of GET /mapTilingService/prices -> { prices: [...] }
// The wire shape nests the money under `pricing` and sends `position` as a GeoJSON Point string.
export class MillPriceOnMap {
    id: number
    mill_id: number
    mill_name?: string
    mill_alias?: string
    mill_full_name?: string
    group_id?: number
    off_platform: boolean

    position?: Position
    coordinates?: number[]
    location_village?: string
    location_subdistrict?: string

    market_date?: string
    price_type?: string
    program_name?: string

    price_per_kg?: number
    program_delta_bonus?: number
    tiers: MillPriceTier[]
    // Best price the mill pays: the flat price or a QBP tier, whichever is higher.
    highest_price_per_kg?: number

    minimum_quality_level?: string
    sorting_strictness?: string
    minimum_bjr?: number
    standard_grading_ratio?: number
    require_spb?: string
    require_spsi: boolean
    accepts_small_truck: boolean
    accepts_dropoff: boolean
    is_brondolan: boolean
    unloading_fee_pre_applied: boolean
    bid_warning_message?: string

    mill_officer_name?: string
    mill_officer_phonenumber?: string

    constructor(p: any) {
        this.id = p.id
        this.mill_id = p.mill_id
        this.mill_name = p.mill_name
        this.mill_alias = p.mill_alias
        this.mill_full_name = p.mill_full_name
        this.group_id = p.group_id
        this.off_platform = p.off_platform ?? false

        this.position = Position.build(parsePosition(p.position))
        this.coordinates = this.position?.coordinates
        this.location_village = p.location_village
        this.location_subdistrict = p.location_subdistrict

        this.market_date = p.market_date
        this.price_type = p.price_type
        this.program_name = p.program_name

        this.price_per_kg = p.pricing?.price_per_kg
        this.program_delta_bonus = p.pricing?.program_delta_bonus
        // Highest cardinality first, so the popup colour ramp always starts on the best tier.
        this.tiers = (p.pricing?.tiers?.map((t: any) => MillPriceTier.build(t)) ?? [])
            .sort((a: MillPriceTier, b: MillPriceTier) => (b.cardinality ?? 0) - (a.cardinality ?? 0))
        this.highest_price_per_kg = highestPrice(this.price_per_kg, this.tiers)

        this.minimum_quality_level = p.minimum_quality_level
        this.sorting_strictness = p.sorting_strictness
        this.minimum_bjr = p.minimum_bjr
        this.standard_grading_ratio = p.standard_grading_ratio
        this.require_spb = p.require_spb
        this.require_spsi = p.require_spsi ?? false
        this.accepts_small_truck = p.accepts_small_truck ?? false
        this.accepts_dropoff = p.accepts_dropoff ?? false
        this.is_brondolan = p.is_brondolan ?? false
        this.unloading_fee_pre_applied = p.unloading_fee_pre_applied ?? false
        this.bid_warning_message = p.bid_warning_message

        this.mill_officer_name = p.mill_officer_name
        this.mill_officer_phonenumber = p.mill_officer_phonenumber
    }

    displayName(): string {
        return this.mill_full_name ?? this.mill_name ?? this.mill_alias ?? ''
    }

    hasPosition(): boolean {
        return this.coordinates !== null && this.coordinates !== undefined
    }

    isQbp(): boolean {
        return this.price_type === "bid_qbp"
    }

    static build(p: any): MillPriceOnMap {
        return new MillPriceOnMap(p)
    }

    static buildAll(prices: any[]): MillPriceOnMap[] {
        return prices?.map(p => MillPriceOnMap.build(p)).filter(p => p.hasPosition() && !p.off_platform) ?? []
    }
}

// Tier totals are on price_pempem; cardinality order says nothing about which tier pays most.
function highestPrice(pricePerKg: number | undefined, tiers: MillPriceTier[]): number | undefined {
    const candidates = [pricePerKg, ...tiers.map(t => t.price_pempem)]
        .filter((p): p is number => typeof p === "number" && !isNaN(p))
    return candidates.length ? Math.max(...candidates) : undefined
}

// `position` is a stringified GeoJSON Point - one malformed row must not lose the whole payload.
function parsePosition(position: any): any {
    if (!position) {
        return undefined
    }
    try {
        const parsed = typeof position === "string" ? JSON.parse(position) : position
        return Array.isArray(parsed?.coordinates) ? parsed : undefined
    } catch (e) {
        console.error("Error parsing price.position:", e)
        return undefined
    }
}

// The map consumers (mapDataFeed / buildPopup) read `feature.properties` + `feature.geometry`,
// so keep handing them a FeatureCollection.
export function toPriceFeatureCollection(prices: MillPriceOnMap[]): any {
    return {
        'type': 'FeatureCollection',
        'features': prices.map(price => ({
            "type": 'Feature',
            "geometry": {
                'type': 'Point',
                'coordinates': price.coordinates
            },
            'properties': price
        }))
    }
}
